import CAButton from '@ds/components/buttons/CAButton'
import CAInput from '@ds/components/form-elements/CAInput'
import ConfirmationDialog from '@vuejs/components/common/ConfirmationDialog'
import api from '@vuejs/services/api'
import {
	deleteContentPackageFolders,
	getContentPackageFolders,
	getContentPackages,
} from '@vuejs/services/packages'
import {
	CONTENT_PACKAGE,
	CONTENT_PACKAGE_FOLDER,
	type ContentPackageFolder,
	type PackageType,
} from '@vuejs/services/packages/interfaces'
import GlobalStore from '@vuejs/stores/GlobalStore'
import { usePackagesAndProgramsStore } from '@vuejs/stores/PackagesAndProgramsStore'
import { pluralize } from '@vuejs/utils/stringUtils'
import { defineComponent } from 'vue'

import type { ViewOption } from '@/javascript/interfaces'

import ListViewFolderView from '../shared/ListViewFolderView'
import LoadingIcon from '../shared/LoadingIcon'
import type { CheckedNodes } from '../shared/TreeView'
import FolderViewPackage from './FolderViewPackage'
import ListViewPackage from './ListViewPackage'
import CreateFolderModal from './modal-content/CreateFolder'
import MoveIntoFolder from './modal-content/MoveIntoFolder'
import RenameFolder from './modal-content/RenameFolder'
import { stringToNumber } from './utils'

function flattenFolder(array: ContentPackageFolder[]): ContentPackageFolder[] {
	return array.flatMap((item: ContentPackageFolder) => item.children ? [
		item,
		...flattenFolder(item.children),
	] : item)
}

export default defineComponent({
	name: 'ContentPackages',

	data(): Data {
		return {
			GlobalStore,
			action: null,
			checkedPackagesAndFolders: [],
			deleteFolders: [],
			foldersAndPackages: [],
			hideBuildingBlocks: true,
			isBuildingBlock: false,
			loading: false,
			message: '',
			parentFolderId: undefined,
			renameFolder: undefined,
			renamedFolderName: '',
			searchInput: undefined,
			selectedItems: [],
			showView: 'list',
			sortBy: undefined,
			sortDirection: undefined,
		}
	},

	computed: {
		currentFolder(): ContentPackageFolder | undefined {
			const selectedFolder = this.packagesAndProgramsStore.allFolders.find(folder => folder.id === this.parentFolderId)

			if (!selectedFolder) {
				return
			}

			return {
				...selectedFolder,
				name: !!this.renamedFolderName ? this.renamedFolderName : selectedFolder.name,
			}
		},

		folders(): ContentPackageFolder[] {
			return flattenFolder(this.packagesAndProgramsStore.allAvailablePackagesAndFolders)
				.filter(f => f.type === 'content_package_folder')
				.map(f => ({
					hasChildren: !!f.children?.length,
					id: f.id,
					level: f.level ?? 0,
					name: f.name,
					type: f.type,
				}))
				.sort((a, b) => a.name.toLowerCase()?.localeCompare(b.name.toLowerCase()))
		},

		hasFoldersToDelete() {
			return !!this.deleteFolders.length
		},

		hasFoldersToPackagesOrFoldersToMove() {
			return !!this.selectedItems.length
		},

		isFolderView() {
			return this.showView === 'folder'
		},

		isListView() {
			return this.showView === 'list'
		},

		moveFolderModalName() {
			return 'move-into-folder-modal'
		},

		numberOfFolders() {
			return this.foldersAndPackages.filter(folder => folder.type === 'content_package_folder').length
		},

		packagesAndProgramsStore() {
			return usePackagesAndProgramsStore()
		},

		selectedFolder(): ContentPackageFolder | undefined {
			const selectedFolderId = this.packagesAndProgramsStore.selectedFolder?.id

			if (!this.packagesAndProgramsStore.selectedFolder || !selectedFolderId) {
				return
			}

			return { ...this.packagesAndProgramsStore.selectedFolder, id: selectedFolderId }
		},

		showBuildingBlocks() {
			if (this.hideBuildingBlocks) {
				return false
			} else if (this.isBuildingBlock) {
				return this.isBuildingBlock
			} else {
				return undefined
			}
		},

		showBulkOptions() {
			return !!this.checkedPackagesAndFolders && !!this.checkedPackagesAndFolders.length
		},
	},

	watch: {
		showView() {
			if (this.isFolderView) {
				this.refreshFolders()
			}
		},
	},

	mounted() {
		this.getContentPackages()
	},

	methods: {
		bulkDeleteFolders() {
			this.openDialog()
		},

		bulkMoveIntoFolder() {
			this.selectedItems = this.checkedPackagesAndFolders

			this.$nextTick(() => {
				GlobalStore.emittedEvent = this.moveFolderModalName
			})
		},

		createFolderForParent() {
			if (this.selectedFolder && typeof this.selectedFolder.id === 'string') {
				this.parentFolderId = stringToNumber(this.selectedFolder.id)
			}

			this.$nextTick(() => {
				GlobalStore.emittedEvent = 'create-content-package-folder-modal'
			})
		},

		async deletePackageFolders() {
			if (!this.hasFoldersToDelete) {
				GlobalStore.onHandleErrorMessages({ error: 'Nothing to delete. Please select a folder and try again.' })

				return
			}

			for (const i of this.deleteFolders) {
				const item = this.packagesAndProgramsStore.allAvailablePackagesAndFolders.find(f => f.id === i.id && f.type === i.type)

				if (!item) {
					GlobalStore.onHandleErrorMessages(
						{ error: 'Unable to find folder to delete. Please refresh and try again, or contact customer support for assistance.' }
					)
				}

				if (item && item?.type === CONTENT_PACKAGE) {
					GlobalStore.onHandleErrorMessages(
						{ error: 'Deleting of content packages are not allowed to be deleted here, and have been skipped. Please contact customer support for further assistance.' }
					)

					if (this.deleteFolders.length === 1) {
						return
					}

					this.deleteFolders = this.deleteFolders.splice(0, 1, item)
				}

				if (item?.hasChildren) {
					GlobalStore.onHandleErrorMessages(
						{ error: `Unable to delete a folder that is not empty. ${item.name} was not deleted and has been skipped.` }
					)

					if (this.deleteFolders.length === 1) {
						return
					}

					this.deleteFolders = this.deleteFolders.splice(0, 1, item)
				}
			}

			try {
				await deleteContentPackageFolders(api, this.deleteFolders.map(i => stringToNumber(i.id ?? '')))
				GlobalStore.onHandleSuccessMessages({ message: 'Selected content package folders have been deleted' })

				this.refreshFolders()
				GlobalStore.resetCAModal()
			} catch (error) {
				GlobalStore.onHandleErrorMessages({ error })
			}
		},

		async getContentPackageFolders(id?: number | string) {
			this.loading = true

			try {
				const response = await getContentPackageFolders(api, {
					includeAll: true,
					parentFolderId: !!id ? stringToNumber(id) : undefined,
				})

				const filteredResponse = response.filter(item => !!item.name)

				this.packagesAndProgramsStore.allAvailablePackagesAndFolders = flattenFolder(filteredResponse)
					.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))

				this.foldersAndPackages = filteredResponse
					.sort((a, b) =>
						a.type > b.type ? -1 : a.type < b.type ? 1 : 0 ||
							a.name.toLowerCase().localeCompare(b.name.toLowerCase())
					)

				if (!id) {
					this.packagesAndProgramsStore.selectedFolder = undefined
				}
			} catch (error) {
				GlobalStore.onHandleErrorMessages({ error })
			} finally {
				this.loading = false
			}
		},

		async getContentPackages(event?: Event) {
			event?.preventDefault()

			if (this.isListView) {
				this.loading = true

				try {
					this.packagesAndProgramsStore.contentPackages = await getContentPackages(api, {
						direction: this.sortDirection,
						format: 'json',
						is_building_block: this.showBuildingBlocks,
						search: this.searchInput,
						sort: this.sortBy,
					})
				} catch (error) {
					GlobalStore.onHandleErrorMessages({ error })
				} finally {
					this.loading = false
				}
			}
		},

		handleAction(action: string, type: PackageType | string, id?: number | string) {
			if (type === CONTENT_PACKAGE_FOLDER && id) {
				this.parentFolderId = id
			}

			if (action === 'Move') {
				this.packagesAndProgramsStore.selectedFolder = this.folders
					.find((folder: ContentPackageFolder) => folder.id === id && folder.type === type)
			}

			switch (action) {
				case 'Create':
					this.createFolderForParent()
					break

				case 'Delete':
					this.deleteFolders.push({ id, type })
					this.openDialog()
					break

				case 'Move':
				case 'Move Package':
					this.moveIntoFolder(action, type, id)
					break

				case 'Rename':
					this.renameContentPackageFolder()
					break
			}
		},

		handleCancelMove() {
			this.selectedItems = []
		},

		handleCheckedItems(checkedNodes: CheckedNodes[]) {
			this.checkedPackagesAndFolders = checkedNodes
			this.deleteFolders = this.checkedPackagesAndFolders
		},

		handleFolderMove(id: number) {
			this.parentFolderId = id
			this.packagesAndProgramsStore.selectedFolder = this.packagesAndProgramsStore.allFolders
				.find((folder: ContentPackageFolder) => folder.id === this.parentFolderId)

			this.refreshFolders()
		},

		handleFolderRename(id: number, name: string) {
			this.parentFolderId = id
			this.renamedFolderName = name
			this.refreshFolders()
		},

		handleKeypress(key: KeyboardEvent): undefined | void {
			this.$nextTick(() => {
				if (key.key === 'Enter') {
					this.getContentPackages()
				}
			})
		},

		hidePackagesBuildingBlocks() {
			this.isBuildingBlock = false
			this.$nextTick(() => {
				this.getContentPackages()
			})
		},

		moveIntoFolder(action: string, type: PackageType | string, id?: number | string) {
			const selectedFolder = this.packagesAndProgramsStore.allAvailablePackagesAndFolders.find(f => f.id === id && f.type === type)
			const selectedItem = this.packagesAndProgramsStore.allAvailablePackagesAndFolders.find(f => f.id === id && f.type === 'content_package')

			if (!selectedFolder && !selectedItem) {
				return
			}

			if (selectedFolder) {
				this.selectedItems?.push({
					id: selectedFolder.id,
					name: selectedFolder.name,
					type: selectedFolder.type,
				})
			}

			if (selectedItem) {
				this.selectedItems?.push({
					id: selectedItem.id,
					name: selectedItem.name,
					type: selectedItem.type,
				})
			}

			GlobalStore.emittedEvent = this.moveFolderModalName
		},

		openDialog() {
			this.message = this.selectedItems.length === 1 ?
				'Confirm that this is a folder and that it is empty before deleting' :
				'Confirm that only folders are selected and that the folders are empty before deleting'

			GlobalStore.emittedEvent = 'bulk-delete-folders'
		},

		refreshFolders() {
			this.deleteFolders = []
			this.parentFolderId = undefined
			this.renameFolder = undefined
			this.renamedFolderName = ''
			this.selectedItems = []

			this.getContentPackageFolders()
			this.packagesAndProgramsStore.getAllContentPackageFolders()
		},

		renameContentPackageFolder() {
			this.renameFolder = this.currentFolder

			this.$nextTick(() => {
				GlobalStore.emittedEvent = 'rename-content-package-folder-modal'
			})
		},

		showPackagesBuildingBlocks() {
			this.hideBuildingBlocks = false
			this.$nextTick(() => {
				this.getContentPackages()
			})
		},
	},

	render() {
		return (
			<div>
				<div class="admin__heading-container">
					<h1 class="admin__heading">Content Packages</h1>

					{this.isListView &&
						<span class="self-end">
							<CAButton
								dataTest="content-packages-new-link"
								text="New package"
								type="primary"
								onClick={() => window.location.assign('/packages/new')}
							/>

							<CAButton
								dataTest="content-packages-download-link"
								text="Download packages report"
								type="primary"
								onClick={() => window.location.assign('/packages/csv')}
							/>
						</span>
					}

					<CreateFolderModal parent-folder-id={this.parentFolderId} onCreated={this.refreshFolders} />
				</div>

				{!this.loading &&
					<div class="flex-container flex-container--space-between">
						<ListViewFolderView onShow={(view: ViewOption): string => this.showView = view} />

						{this.showBulkOptions &&
							<p>
								<span>
									<CAButton
										class="ml-2"
										dataTest="content-packages-bulk-move"
										text="Move folder(s)"
										onClick={this.bulkMoveIntoFolder}
									/>

									{this.hasFoldersToDelete &&
										<CAButton
											class="ml-2"
											dataTest="content-packages-bulk-delete"
											text="Delete folder(s)"
											type="danger"
											onClick={this.bulkDeleteFolders}
										/>
									}
								</span>
							</p>
						}
					</div>
				}

				<hr class="clear clearfix" />

				{this.isListView &&
					<div>
						<div class="form-inline">
							<div class="form-check">
								<input
									data-test="building-block-checkbox"
									id="is_building_block"
									name="is_building_block"
									type="checkbox"
									v-model={this.isBuildingBlock}
									onChange={this.showPackagesBuildingBlocks}
								/>
								<label for="is_building_block">Building Blocks</label>
							</div>

							<div class="form-check ml-2">
								<input
									data-test="hide-building-block-checkbox"
									id="hide_building_block"
									name="hide_building_block"
									type="checkbox"
									v-model={this.hideBuildingBlocks}
									onChange={this.hidePackagesBuildingBlocks}
								/>
								<label for="hide_building_block">Hide Building Blocks</label>
							</div>

							<CAInput
								class="mb-0 mr-3"
								dataTest="search-input"
								icon="SVGSearch"
								placeholder="Search by package name"
								sustain-label
								v-model={this.searchInput}
							/>

							<CAButton
								data-disable-with="Filter"
								dataTest="content-packages-filter-button"
								text="Filter"
								onClick={this.getContentPackages}
							/>
						</div>

						<hr class="clear clearfix" />
					</div>
				}

				{this.loading ?
					<div class="horizontal-vertical-center-container mt5">
						<LoadingIcon size="large" />
					</div>

					:

					<div>
						{this.isFolderView &&
							<div>
								{!this.foldersAndPackages.length ?
									<div class="container__no-results" data-test="content-packages-no-results">
										<p>Currently no folders or packages to display.</p>
									</div>

									:

									<div>
										<FolderViewPackage
											folders={this.foldersAndPackages}
											onAction={(action: string, type: PackageType | string, id?: number | string): void =>
												this.handleAction(action, type, id)}
											onBulk={(checkedNodes: CheckedNodes[]): void => this.handleCheckedItems(checkedNodes)}
										/>

										{this.hasFoldersToPackagesOrFoldersToMove &&
											<MoveIntoFolder
												folders={this.folders}
												items={this.selectedItems}
												onCancel={this.handleCancelMove}
												onMove={(id: number | null): void => { if (id !== null) this.handleFolderMove(id) }}
											/>
										}

										{this.folders && this.renameFolder &&
											<RenameFolder
												folder={this.renameFolder}
												onCancel={() => this.renameFolder = undefined}
												onRenamed={(id: number, name: string): void => this.handleFolderRename(id, name)}
											/>
										}
									</div>
								}
							</div>
						}

						{this.isListView &&
							<ListViewPackage onAction={(action: string): string => this.action = action} />
						}

						<p>
							List for <b>all {this.packagesAndProgramsStore.contentPackages.length}</b> packages
							{this.isFolderView && <span> and <b>{this.numberOfFolders}</b> {pluralize(this.numberOfFolders, 'folder')}</span>}
						</p>
					</div>
				}

				<ConfirmationDialog
					dataTest="bulk-delete-folders"
					modalStyle="danger"
					name="bulk-delete-folders"
					submitText="Delete"
					onCancel={() => this.message = ''}
					onConfirm={this.deletePackageFolders}
				>
					<p>{this.message}</p>
				</ConfirmationDialog>
			</div>
		)
	},
})

interface Data {
	GlobalStore: typeof GlobalStore
	action: string | null
	checkedPackagesAndFolders: CheckedNodes[]
	deleteFolders: CheckedNodes[]
	foldersAndPackages: ContentPackageFolder[]
	hideBuildingBlocks: boolean
	isBuildingBlock: boolean | undefined
	loading: boolean
	message: string
	parentFolderId: number | string | undefined
	renameFolder: ContentPackageFolder | undefined
	renamedFolderName: string
	searchInput: string | undefined
	selectedItems: CheckedNodes[]
	showView: ViewOption
	sortBy: 'name' | undefined
	sortDirection: 'asc' | 'desc' | undefined
}
